import React, { useEffect, useRef, useState } from "react";
import { useIsDesktop } from "./desktopUi";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image as ChakraImage,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { appImages } from "../assets/appicons";

// =============================================================
// Bộ UI dùng chung cho các màn "Quản lý" của cổng nhân viên (web mobile).
// Dựng theo đúng các component tương ứng của mobile-app-v2:
//  - StaffFilterField / StatisticalReportFilterField  -> FilterField
//  - "Bộ lọc chi tiết" collapse header + panel        -> FilterPanel
//  - StaffSummaryCard / *SummaryCard                  -> SummaryCard
//  - "Danh sách ... + sắp xếp"                        -> ListHeaderRow
//  - BottomSheetSelectModal                           -> SelectSheet
//  - ActionPopover (Phê duyệt / Từ chối)              -> ActionPopover
//  - CustomerTypeCard (thẻ thống kê KPI)              -> StatCard
// =============================================================

export const M = {
  primary: "#4081EB",
  text: "#08142D",
  baseContent: "#18212F",
  subTitle: "#51607B",
  tertiary: "#94A3B8",
  grey200: "#A4AFC1",
  gray: "#808CA0",
  border: "#E1E1E1",
  line: "#EEF1F6",
  field: "#F7F9FC",
  green: "#0BA859",
  green300: "#2ABA82",
  red: "#E5484D",
  orange: "#F79D0D",
};

export const ALL_VALUE = "ALL";

/* ------------------------------------------------------------------ icon */
export const Png = ({ src, size = 18, color, style }) => {
  if (color) {
    return (
      <Box
        as="span"
        display="inline-block"
        flexShrink={0}
        w={`${size}px`}
        h={`${size}px`}
        bg={color}
        style={{
          WebkitMaskImage: `url(${src})`,
          maskImage: `url(${src})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          ...style,
        }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      style={{ objectFit: "contain", display: "block", flexShrink: 0, ...style }}
    />
  );
};

/* --------------------------------------------------------------- search */
export const SearchBox = ({ placeholder = "Nhập tên nhân viên ...", onChange, delay = 300, radius = "12px" }) => {
  const timer = useRef(null);
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);
  return (
    <Flex
      h="44px"
      align="center"
      px="12px"
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius={radius}
    >
      <Png src={appImages.icon_search} size={20} color={M.grey200} />
      <Input
        variant="unstyled"
        ml="8px"
        fontSize="14px"
        fontWeight={600}
        color={M.baseContent}
        placeholder={placeholder}
        _placeholder={{ color: M.grey200, fontWeight: 400 }}
        onChange={(e) => {
          const v = e.target.value;
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => onChange?.(v.trim()), delay);
        }}
      />
    </Flex>
  );
};

/* ---------------------------------------------------------- filter field */
// Khớp StaffFilterField của app: nhãn nhỏ + hộp nền xám bo 8 + chevron.
export const FilterField = ({ label, value, onClick, width = "48%" }) => (
  <Box as="button" type="button" onClick={onClick} w={width} textAlign="left">
    <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
      {label}
    </Text>
    <Box h="6px" />
    <Flex align="center" justify="space-between" bg={M.field} borderRadius="8px" p="8px" h="38px">
      <Text fontSize="14px" fontWeight={500} color={M.baseContent} noOfLines={1} flex="1" textAlign="left">
        {value}
      </Text>
      <Png src={appImages.icon_chevron_down} size={18} />
    </Flex>
  </Box>
);

// Ô chọn ngày: hiện DD/MM/YY như app, input date native ẩn phía trên.
export const DateFilterField = ({ label, value, onChange, width = "48%", placeholder = "Chọn ngày" }) => (
  <Box w={width} position="relative">
    <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
      {label}
    </Text>
    <Box h="6px" />
    <Flex align="center" justify="space-between" bg={M.field} borderRadius="8px" p="8px" h="38px">
      <Text fontSize="14px" fontWeight={500} color={value ? M.baseContent : M.grey200} noOfLines={1}>
        {value ? moment(value).format("DD/MM/YY") : placeholder}
      </Text>
      <Png src={appImages.icon_chevron_down} size={18} />
    </Flex>
    <Input
      type="date"
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      position="absolute"
      left="0"
      right="0"
      bottom="0"
      h="38px"
      w="100%"
      opacity={0}
      p="0"
      cursor="pointer"
    />
  </Box>
);

/* --------------------------------------------------------- filter panel */
// Header "Bộ lọc chi tiết" + panel bung xuống (khớp app: bo 12, viền dính liền).
export const FilterPanel = ({ open, onToggle, children, topRadius = true }) => (
  <Box>
    <Flex
      as="button"
      type="button"
      w="100%"
      h="44px"
      px="12px"
      align="center"
      justify="space-between"
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderBottomWidth={open ? "0" : "1px"}
      borderTopRadius={topRadius ? "12px" : "0"}
      borderBottomRadius={open ? "0" : "12px"}
      onClick={onToggle}
    >
      <Flex align="center" gap="8px">
        <Png src={appImages.icon_filter_detail} size={18} />
        <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
          Bộ lọc chi tiết
        </Text>
      </Flex>
      <Png src={open ? appImages.icon_chevron_up : appImages.icon_chevron_down} size={18} />
    </Flex>
    <Collapse in={open} animateOpacity>
      <Box
        bg="white"
        border="1px solid"
        borderColor={M.border}
        borderTop="0"
        borderBottomRadius="12px"
        p="12px"
      >
        <Flex flexWrap="wrap" gap="10px" justify="space-between">
          {children}
        </Flex>
      </Box>
    </Collapse>
  </Box>
);

/* --------------------------------------------------------- summary card */
export const SummaryCard = ({ text, total }) => (
  <Box bg="white" border="1px solid" borderColor={M.border} borderRadius="12px" p="14px">
    <Text fontSize="12px" fontWeight={500} color={M.tertiary}>
      {text}
    </Text>
    <Box h="8px" />
    <Text fontSize="20px" fontWeight={700} color={M.primary}>
      {String(total ?? 0)}
    </Text>
  </Box>
);

/* ----------------------------------------------------- list header + sort */
export const ListHeaderRow = ({ title, sortLabel, onSort, mt = "16px", mb = "14px" }) => (
  <Flex align="center" justify="space-between" mt={mt} mb={mb} gap="8px">
    <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
      {title}
    </Text>
    {sortLabel ? (
      <Flex as="button" type="button" align="center" gap="6px" onClick={onSort} flexShrink={0}>
        <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
          {sortLabel}
        </Text>
        <Png src={appImages.ion_sort_desc} size={16} />
      </Flex>
    ) : null}
  </Flex>
);

/* ----------------------------------------------------------------- rows */
export const RowKV = ({ label, value, valueColor, labelColor, labelFlex = "0.42", multiline = true }) => (
  <Flex align="flex-start" gap="12px">
    <Text fontSize="14px" fontWeight={500} color={labelColor || M.tertiary} flex={labelFlex}>
      {label}
    </Text>
    <Text
      fontSize="14px"
      fontWeight={600}
      color={valueColor || M.baseContent}
      flex="0.58"
      textAlign="right"
      whiteSpace="pre-line"
      noOfLines={multiline ? 3 : 1}
    >
      {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
    </Text>
  </Flex>
);

// Dòng chi tiết có gạch dưới (khớp *PropCell của app).
export const PropCell = ({ label, value, borderBottom = true, onPress, color, isCopy }) => {
  const display =
    value !== undefined && value !== null && value !== "" ? String(value) : "-";
  const handle = () => {
    if (isCopy) {
      if (display === "-") return;
      try {
        navigator.clipboard?.writeText(display);
      } catch {
        /* noop */
      }
      return;
    }
    onPress?.();
  };
  return (
    <Flex
      py="18px"
      justify="space-between"
      align="flex-start"
      gap="12px"
      borderBottom={borderBottom ? "0.5px solid" : "none"}
      borderColor={M.border}
    >
      <Text fontSize="14px" color={M.subTitle} flex="0.42">
        {label}
      </Text>
      <Box flex="0.58" textAlign="right">
        <Text
          as={onPress || isCopy ? "button" : "span"}
          onClick={onPress || isCopy ? handle : undefined}
          fontSize="14px"
          fontWeight={600}
          color={onPress ? M.primary : color || M.baseContent}
          whiteSpace="pre-line"
          textAlign="right"
        >
          {display}
        </Text>
      </Box>
    </Flex>
  );
};

/* ------------------------------------------------------- expandable card */
// Thẻ danh sách bấm để bung chi tiết (khớp các *ListCard của app).
export const ExpandableCard = ({ header, open, onToggle, children, onDetailClick }) => (
  <Box bg="white" border="1px solid" borderColor={M.border} borderRadius="8px">
    <Flex
      as="button"
      type="button"
      w="100%"
      align="center"
      gap="12px"
      p="16px"
      textAlign="left"
      onClick={onToggle}
    >
      <Box flex="1" minW="0">
        {header}
      </Box>
      <Png src={open ? appImages.icon_chevron_down : appImages.icon_chevron_right} size={18} />
    </Flex>
    {open ? (
      <Box borderTop="1px solid" borderColor={M.line}>
        <Flex
          direction="column"
          gap="10px"
          p="16px"
          cursor={onDetailClick ? "pointer" : "default"}
          onClick={onDetailClick}
        >
          {children}
        </Flex>
      </Box>
    ) : null}
  </Box>
);

/* ------------------------------------------------------------ stat card */
// Khớp CustomerTypeCard trong màn KPI nhân viên.
export const StatCard = ({ subTitle, title, value, icon, bgColor, textColor, width = "calc(50% - 6px)" }) => (
  <Box w={width} minH="110px" borderRadius="8px" p="16px" border="1px solid" borderColor={M.border} bg="white">
    <Flex align="center" gap="12px">
      <Flex w="24px" h="24px" borderRadius="4px" align="center" justify="center" bg={bgColor}>
        <Png src={icon} size={12} color={textColor} />
      </Flex>
      <Text fontSize="10px" fontWeight={600} color={M.baseContent}>
        {subTitle}
      </Text>
    </Flex>
    <Text fontSize="16px" fontWeight={700} color={M.baseContent} mt="4px">
      {String(value ?? 0)}
    </Text>
    <Text fontSize="12px" fontWeight={500} color={M.gray} mt="4px">
      {title}
    </Text>
  </Box>
);

/* ---------------------------------------------------------- select sheet */
/**
 * Bottom sheet chọn 1 giá trị (khớp BottomSheetSelectModal của app).
 * data: [{ key, label, raw }]
 */
export const SelectSheet = ({
  isOpen,
  onClose,
  title,
  data = [],
  selectedKey,
  onSelect,
  searchable = false,
  searchValue,
  onChangeSearch,
  loading = false,
  loadingMore = false,
  hasNextPage = false,
  onEndReached,
}) => (
  <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
    <DrawerOverlay bg="rgba(0,0,0,0.45)" />
    <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
      <DrawerBody py="16px" px="16px">
        <Flex align="center" justify="space-between" mb="8px">
          <Text fontSize="16px" fontWeight={700} color={M.baseContent}>
            {title}
          </Text>
          <Box as="button" type="button" onClick={onClose}>
            <Png src={appImages.icon_close} size={20} />
          </Box>
        </Flex>

        {searchable ? (
          <Flex h="40px" align="center" px="12px" bg={M.field} borderRadius="8px" mb="8px">
            <Png src={appImages.icon_search} size={18} color={M.grey200} />
            <Input
              variant="unstyled"
              ml="8px"
              fontSize="14px"
              placeholder="Tìm kiếm ..."
              value={searchValue || ""}
              onChange={(e) => onChangeSearch?.(e.target.value)}
            />
          </Flex>
        ) : null}

        <Box maxH="46vh" overflowY="auto">
          {loading ? (
            <Flex justify="center" py="24px">
              <Spinner size="sm" color={M.primary} />
            </Flex>
          ) : data.length === 0 ? (
            <Text fontSize="14px" color={M.tertiary} textAlign="center" py="24px">
              Không có dữ liệu
            </Text>
          ) : (
            data.map((o, i) => {
              const active = String(o.key) === String(selectedKey);
              const last = i === data.length - 1;
              return (
                <Flex
                  key={`${o.key}-${i}`}
                  as="button"
                  type="button"
                  w="100%"
                  align="center"
                  justify="space-between"
                  py="12px"
                  gap="12px"
                  borderBottom={last ? "none" : `1px solid ${M.line}`}
                  onClick={() => onSelect?.(o.raw !== undefined ? o.raw : o.key)}
                >
                  <Text
                    fontSize="15px"
                    textAlign="left"
                    color={active ? M.primary : M.baseContent}
                    fontWeight={active ? 600 : 400}
                  >
                    {o.label}
                  </Text>
                  {active ? <Png src={appImages.icon_check} size={18} /> : null}
                </Flex>
              );
            })
          )}

          {hasNextPage && !loading ? (
            <Flex as="button" type="button" w="100%" justify="center" py="12px" onClick={onEndReached}>
              {loadingMore ? (
                <Spinner size="sm" color={M.primary} />
              ) : (
                <Text fontSize="14px" fontWeight={600} color={M.primary}>
                  Xem thêm
                </Text>
              )}
            </Flex>
          ) : null}
        </Box>
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

/* -------------------------------------------------------- action popover */
/**
 * Menu hành động thả xuống từ nút trên card (khớp ActionPopover của app).
 * anchor: DOMRect của nút bấm.
 */
export const ActionPopover = ({ open, onClose, anchor, items = [] }) => {
  if (!open || !anchor) return null;
  const WIDTH = 180;
  const ROW = 50;
  const menuHeight = Math.max(items.length * ROW, ROW);
  const preferLeft = anchor.left + anchor.width - WIDTH;
  const left = Math.min(Math.max(12, preferLeft), Math.max(12, window.innerWidth - WIDTH - 12));
  const preferTop = anchor.top + anchor.height + 6;
  const showAbove = preferTop + menuHeight > window.innerHeight - 12;
  const top = showAbove ? Math.max(12, anchor.top - menuHeight - 6) : preferTop;

  return (
    <Box position="fixed" inset="0" zIndex={2000} bg="rgba(0,0,0,0.12)" onClick={onClose}>
      <Box
        position="absolute"
        top={`${top}px`}
        left={`${left}px`}
        w={`${WIDTH}px`}
        bg="white"
        borderRadius="12px"
        border="1px solid"
        borderColor={M.border}
        boxShadow="0 8px 18px rgba(0,0,0,0.12)"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((a, i) => (
          <Flex
            key={a.key}
            as="button"
            type="button"
            w="100%"
            align="center"
            gap="10px"
            px="14px"
            py="14px"
            borderBottom={i === items.length - 1 ? "none" : `0.5px solid ${M.border}`}
            onClick={() => {
              onClose?.();
              a.onPress?.();
            }}
          >
            {a.icon ? <Png src={a.icon} size={18} /> : null}
            <Text fontSize="14px" color={a.danger ? M.red : M.baseContent}>
              {a.label}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

// Nút mở menu hành động trên card (icon_edit_customer nền xanh nhạt).
export const ActionButton = ({ onOpen }) => (
  <Box
    as="button"
    type="button"
    w="44px"
    h="44px"
    borderRadius="6px"
    bg="#EFF6FF"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    onClick={(e) => {
      e.stopPropagation();
      onOpen?.(e.currentTarget.getBoundingClientRect());
    }}
  >
    <Png src={appImages.icon_edit_customer} size={24} />
  </Box>
);

/* ------------------------------------------------------------- fragments */
export const Avatar = ({ src, size = 40, radius }) => (
  <ChakraImage
    src={src || appImages.icon_avatar}
    fallbackSrc={appImages.icon_avatar}
    alt=""
    w={`${size}px`}
    h={`${size}px`}
    borderRadius={radius ?? `${size / 2}px`}
    objectFit="cover"
    flexShrink={0}
  />
);

export const StatusChip = ({ label, bg, color, icon }) => (
  <Flex
    display="inline-flex"
    align="center"
    alignSelf="flex-start"
    bg={bg}
    borderRadius="6px"
    px="8px"
    py="3px"
    gap="8px"
    flexShrink={0}
  >
    {icon ? <Png src={icon} size={18} /> : null}
    <Text fontSize="12px" fontWeight={600} color={color} whiteSpace="nowrap">
      {label}
    </Text>
  </Flex>
);

export const LoadMore = ({ hasNext, loading, onClick }) => {
  if (!hasNext) return null;
  return (
    <Flex as="button" type="button" onClick={onClick} justify="center" py="12px">
      {loading ? (
        <Spinner size="sm" color={M.primary} />
      ) : (
        <Text fontSize="14px" fontWeight={600} color={M.primary}>
          Xem thêm
        </Text>
      )}
    </Flex>
  );
};

export const EmptyText = ({ text = "Không có dữ liệu" }) => (
  <Flex justify="center" py="40px">
    <Text fontSize="14px" color={M.gray}>
      {text}
    </Text>
  </Flex>
);

export const CenterSpinner = () => (
  <Flex justify="center" py="32px">
    <Spinner color={M.primary} />
  </Flex>
);

/* ------------------------------------------------------ month/year picker */
export const buildMonthOptions = (count = 24) => {
  const out = [];
  const now = moment();
  for (let i = 0; i < count; i += 1) {
    const m = now.clone().subtract(i, "months");
    out.push({ value: m.format("MM-YYYY"), label: `Tháng ${m.format("M/YYYY")}` });
  }
  return out;
};

/* ------------------------------------------------------------ menu grid */
// Lưới 2 cột các thẻ chức năng (khớp MenuCard của app: icon PNG tint primary200).
export const MenuCard = ({ icon, label, onClick }) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    w={{ base: "calc(50% - 6px)", md: "100%" }}
    h="96px"
    bg="white"
    border="1px solid"
    borderColor={M.border}
    borderRadius="12px"
    px="14px"
    py="14px"
    textAlign="left"
    boxShadow="0 5px 5px rgba(0,0,0,0.04)"
  >
    <Flex direction="column" justify="space-between" h="100%">
      <Flex w="32px" h="32px" align="center" justify="center">
        <Png src={icon} size={32} color={M.primary} />
      </Flex>
      <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
        {label}
      </Text>
    </Flex>
  </Box>
);

// Mobile: lưới 2 cột như app. Desktop: lưới thẻ tự giãn theo style admin.
// Chỉ render đúng một nhánh để không nhân đôi DOM/state của thẻ con.
export const MenuGrid = ({ children }) => {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
        gap="16px"
        bg="var(--color-main)"
        borderRadius="12px"
        p="16px"
      >
        {children}
      </Box>
    );
  }

  return (
    <Flex flexWrap="wrap" gap="12px" px="16px" pt="12px">
      {children}
    </Flex>
  );
};

/* --------------------------------------------------------- hook: sheet */
export const useSheet = () => {
  const [type, setType] = useState(null);
  return {
    type,
    isOpen: !!type,
    open: (t) => setType(t),
    close: () => setType(null),
  };
};
